// src/pages/events/EventDetails.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '@clerk/clerk-react';
import { eventsAPI } from '../../services/api';
import { isEventPast } from '../../utils/eventDate';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';
import * as htmlToImage from 'html-to-image';
import {
  FaCalendarAlt,
  FaTrophy,
  FaShare,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaWhatsapp,
  FaCopy,
  FaCheckCircle,
  FaSpinner,
  FaInfoCircle,
  FaQrcode,
  FaDownload,
  FaTimes,
  FaClock,
  FaMapMarkerAlt,
  FaUsers,
  FaDollarSign,
  FaSchool,
  FaStar,
  FaGlobe,
  FaMedal,
  FaAward
} from 'react-icons/fa';

const EventDetails = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { isSignedIn, isLoaded } = useUser();
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);
  
  const qrCodeRef = useRef(null);

  // Use shared isEventPast(event) util imported above

  // Fetch event details
  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);
        const response = await eventsAPI.getById(eventId);
        
        if (response.success) {
          setEvent(response.event);
          
          // Check if user is registered
          if (isSignedIn) {
            const userEventsResponse = await eventsAPI.getUserEvents();
            const userEvents = userEventsResponse.events || [];
            const isUserRegistered = userEvents.some(ue => ue._id === eventId);
            setIsRegistered(isUserRegistered);
          }
        } else {
          throw new Error('Event not found');
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        toast.error('Failed to load event details');
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEventDetails();
    }
  }, [eventId, isSignedIn, navigate]);

  // Handle event registration
  const handleRegister = async () => {
    if (!isSignedIn) {
      toast.error('Please login to register for events');
      navigate('/login');
      return;
    }

    if (isRegistered) {
      toast.info('You are already registered for this event');
      return;
    }

    // Prevent registering for past events
    if (isEventPast(event)) {
      toast.info('This event has already ended and cannot be registered for.');
      return;
    }

    try {
      setRegistering(true);
      const response = await eventsAPI.register(eventId);
      
      if (response.success) {
        setIsRegistered(true);
        toast.success('Successfully registered for the event!');
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to register for event');
    } finally {
      setRegistering(false);
    }
  };

  // Download QR code
  const downloadQRCode = async () => {
    if (!qrCodeRef.current) return;

    try {
      setGeneratingQR(true);
      
      const dataUrl = await htmlToImage.toPng(qrCodeRef.current, {
        backgroundColor: 'white',
        width: 300,
        height: 350,
        style: { padding: '20px' }
      });

      const link = document.createElement('a');
      link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '-')}-QRCode.png`;
      link.href = dataUrl;
      link.click();

      toast.success('QR code downloaded successfully!');
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to download QR code');
    } finally {
      setGeneratingQR(false);
    }
  };

  // Share functionality
  const currentUrl = window.location.href;
  const shareText = `Check out this event: ${event?.title || 'Event'}`;

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(shareText)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + currentUrl)}`
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl);
    setCopySuccess(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <FaSpinner className="w-8 h-8 text-green-500 animate-spin" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Event not found</p>
          <Link to="/events" className="text-green-500 hover:underline">
            Back to Events
          </Link>
        </div>
      </div>
    );
  }

  // Get event type color
  const getEventTypeColor = (type) => {
    const colors = {
      tournament: 'from-purple-500 to-purple-600',
      workshop: 'from-blue-500 to-blue-600',
      training: 'from-orange-500 to-orange-600',
      competition: 'from-red-500 to-red-600'
    };
    return colors[type] || 'from-gray-500 to-gray-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={event.thumbnail || 'https://images.unsplash.com/photo-1577896851231-70ef18881754?w=1200&h=800&fit=crop'}
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        </div>

        {/* Share & QR Buttons */}
        <div className="absolute top-6 right-6 z-20 flex space-x-3">
          <button
            onClick={() => setShowQRModal(true)}
            className="px-4 py-2 bg-white/10 backdrop-blur-md text-white rounded-lg hover:bg-white/20 transition-all duration-300 flex items-center gap-2 shadow-lg"
          >
            <FaQrcode />
            <span className="text-sm font-medium">QR</span>
          </button>

          <div className="relative">
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="px-4 py-2 bg-white/10 backdrop-blur-md text-white rounded-lg hover:bg-white/20 transition-all duration-300 flex items-center gap-2 shadow-lg"
            >
              <FaShare />
              <span className="text-sm font-medium">Share</span>
            </button>

            {showShareMenu && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 p-2 z-30 min-w-[180px]"
              >
                <a href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors">
                  <FaFacebook className="text-blue-600" />
                  <span className="text-gray-700 text-sm">Facebook</span>
                </a>
                <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 hover:bg-sky-50 rounded-lg transition-colors">
                  <FaTwitter className="text-sky-500" />
                  <span className="text-gray-700 text-sm">Twitter</span>
                </a>
                <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors">
                  <FaLinkedin className="text-blue-700" />
                  <span className="text-gray-700 text-sm">LinkedIn</span>
                </a>
                <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 hover:bg-green-50 rounded-lg transition-colors">
                  <FaWhatsapp className="text-green-600" />
                  <span className="text-gray-700 text-sm">WhatsApp</span>
                </a>
                <button onClick={copyToClipboard} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-lg transition-colors w-full">
                  {copySuccess ? <FaCheckCircle className="text-green-500" /> : <FaCopy className="text-gray-600" />}
                  <span className="text-gray-700 text-sm">{copySuccess ? 'Copied!' : 'Copy Link'}</span>
                </button>
              </motion.div>
            )}
          </div>
        </div>

        {/* Event Info Overlay */}
        <div className="absolute inset-0 flex items-end z-10">
          <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className={`px-4 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r ${getEventTypeColor(event.type)} shadow-lg`}>
                  {event.type?.toUpperCase() || 'EVENT'}
                </span>
                
                {event.featured && (
                  <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-yellow-500 text-white shadow-lg flex items-center gap-1">
                    <FaStar className="w-3 h-3" />
                    FEATURED
                  </span>
                )}
                
                {isRegistered && (
                  <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-green-500 text-white shadow-lg flex items-center gap-1">
                    <FaCheckCircle className="w-3 h-3" />
                    REGISTERED
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                {event.title}
              </h1>

              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <FaCalendarAlt className="text-white/80" />
                    <p className="text-white/80 text-xs">Start Date</p>
                  </div>
                  <p className="font-bold">{event.date ? new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBA'}</p>
                </div>

                {event.endDate && (
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <FaCalendarAlt className="text-white/80" />
                      <p className="text-white/80 text-xs">End Date</p>
                    </div>
                    <p className="font-bold">{new Date(event.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                )}

                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <FaClock className="text-white/80" />
                    <p className="text-white/80 text-xs">Time</p>
                  </div>
                  <p className="font-bold">{event.time || 'TBA'}</p>
                </div>

                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <FaMapMarkerAlt className="text-white/80" />
                    <p className="text-white/80 text-xs">Location</p>
                  </div>
                  <p className="font-bold text-sm">{event.location || 'TBA'}</p>
                </div>
              </div>

              {/* Action Button */}
              <div className="flex flex-wrap gap-3 items-center">
                {isEventPast(event) ? (
                  <div className="px-8 py-4 rounded-full font-bold bg-gray-400 text-white cursor-not-allowed flex items-center gap-2">
                    <FaTimes /> Event Ended
                  </div>
                ) : (
                  <button
                    onClick={handleRegister}
                    disabled={registering || isRegistered}
                    className={`px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-xl flex items-center gap-2 ${
                      isRegistered
                        ? 'bg-green-500 text-white cursor-default'
                        : 'bg-white text-gray-900 hover:bg-gray-100 hover:scale-105'
                    }`}
                  >
                    {registering ? (
                      <><FaSpinner className="animate-spin" /> Registering...</>
                    ) : isRegistered ? (
                      <><FaCheckCircle /> Registered</>
                    ) : (
                      <><FaCalendarAlt /> Register Now</>
                    )}
                  </button>
                )}

                {event.registrationFee && (
                  <div className="px-6 py-4 bg-white/10 backdrop-blur-md rounded-full font-bold">
                    <span className="text-white/80 text-sm">Fee: </span>
                    <span className="text-white">{event.registrationFee}</span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* QR Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-sm w-full"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Share Event</h3>
              <button onClick={() => setShowQRModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <FaTimes />
              </button>
            </div>

            <div ref={qrCodeRef} className="text-center mb-6">
              <div className="bg-white p-4 rounded-lg inline-block">
                <QRCode size={200} value={currentUrl} viewBox="0 0 256 256" level="M" />
              </div>
              <div className="mt-3">
                <p className="font-semibold text-gray-900">{event.title}</p>
                <p className="text-gray-600 text-sm">{event.type} • {event.date ? new Date(event.date).toLocaleDateString() : 'TBA'}</p>
              </div>
            </div>

            <button
              onClick={downloadQRCode}
              disabled={generatingQR}
              className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
                generatingQR ? 'bg-gray-300 text-gray-500' : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {generatingQR ? <><FaSpinner className="animate-spin" /> Generating...</> : <><FaDownload /> Download QR Code</>}
            </button>
          </motion.div>
        </div>
      )}

      {/* Event Details Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* About Event */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <FaInfoCircle className="text-green-500" />
                  About This Event
                </h2>
                
                <p className="text-gray-700 leading-relaxed text-base whitespace-pre-wrap">
                  {event.description || 'Join us for an exciting event filled with learning, networking, and growth opportunities.'}
                </p>
              </motion.div>

              {/* Prize Information */}
              {event.prize && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-8 shadow-lg border border-yellow-100"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaTrophy className="text-yellow-500" />
                    Prizes & Awards
                  </h2>
                  
                  <p className="text-gray-700 leading-relaxed">{event.prize}</p>
                </motion.div>
              )}

              {/* Levels */}
              {event.levels && event.levels.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaMedal className="text-blue-500" />
                    Competition Levels
                  </h2>
                  
                  <div className="flex flex-wrap gap-3">
                    {event.levels.map((level, idx) => (
                      <span key={idx} className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm border border-blue-200">
                        {level}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Timezone Info */}
              {event.timezone && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 shadow-lg border border-indigo-100"
                >
                  <div className="flex items-center gap-3">
                    <FaGlobe className="text-indigo-500 text-2xl" />
                    <div>
                      <p className="text-sm text-gray-600">Event Timezone</p>
                      <p className="font-bold text-gray-900">{event.timezone}</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6 sticky top-24"
              >
                {/* Event Stats */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Event Details</h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                      <span className="text-gray-600 text-sm flex items-center gap-2">
                        <FaUsers className="text-gray-400" />
                        Max Participants
                      </span>
                      <span className="font-bold text-gray-900">{event.maxParticipants || '∞'}</span>
                    </div>

                    {event.maxInstitutions && (
                      <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                        <span className="text-gray-600 text-sm flex items-center gap-2">
                          <FaSchool className="text-gray-400" />
                          Max Institutions
                        </span>
                        <span className="font-bold text-gray-900">{event.maxInstitutions}</span>
                      </div>
                    )}

                    {event.registrationFee && (
                      <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                        <span className="text-gray-600 text-sm flex items-center gap-2">
                          <FaDollarSign className="text-gray-400" />
                          Registration Fee
                        </span>
                        <span className="font-bold text-green-600">{event.registrationFee}</span>
                      </div>
                    )}

                    {event.paymentRequired !== undefined && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 text-sm">Payment Required</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          event.paymentRequired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {event.paymentRequired ? 'Yes' : 'No'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Time Details */}
                {(event.time || event.endTime) && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 shadow-lg border border-green-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <FaClock className="text-green-500" />
                      Schedule
                    </h3>
                    
                    <div className="space-y-3">
                      {event.time && (
                        <div>
                          <p className="text-gray-600 text-xs mb-1">Start Time</p>
                          <p className="font-bold text-gray-900">{event.time}</p>
                        </div>
                      )}
                      {event.endTime && (
                        <div>
                          <p className="text-gray-600 text-xs mb-1">End Time</p>
                          <p className="font-bold text-gray-900">{event.endTime}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact & Links */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Explore More</h3>
                  
                  <div className="space-y-3">
                    <Link to="/events" className="block p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all hover:shadow-md">
                      <p className="font-semibold text-gray-900 text-sm mb-1">Browse All Events</p>
                      <p className="text-xs text-gray-600">Discover more competitions</p>
                    </Link>
                    <Link to="/programs" className="block p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-all hover:shadow-md">
                      <p className="font-semibold text-green-900 text-sm mb-1">View Programs</p>
                      <p className="text-xs text-green-600">Join training programs</p>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EventDetails;
